import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

/* Theme has THREE states, not two. "system" is the default and is not the same
   as light: it must leave data-theme unset so prefers-color-scheme decides. A
   two-state toggle that writes data-theme="light" on first render silently
   opts every user out of their operating system's dark mode. */

export type ThemeChoice = 'system' | 'light' | 'dark'

const KEY = 'hk.theme'

interface ThemeContextValue {
  choice: ThemeChoice
  setChoice: (c: ThemeChoice) => void
}

const ThemeContext = createContext<ThemeContextValue>({ choice: 'system', setChoice: () => {} })

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch { /* private mode, blocked storage, thumbnail capture — all fine */ }
  return 'system'
}

function apply(choice: ThemeChoice) {
  const el = document.documentElement
  if (choice === 'system') el.removeAttribute('data-theme')
  else el.setAttribute('data-theme', choice)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(read)

  useEffect(() => { apply(choice) }, [choice])

  const setChoice = useCallback((c: ThemeChoice) => {
    setChoiceState(c)
    try { localStorage.setItem(KEY, c) } catch { /* non-fatal */ }
  }, [])

  return <ThemeContext value={{ choice, setChoice }}>{children}</ThemeContext>
}

export const useTheme = () => useContext(ThemeContext)
